import { hasStoreAgencyIdentity } from '~/store/common/user'
import { getRuntimeAuthKeys } from '~/composables/useStorageKey'
import { getAuthCookieOptions } from '~/utils/storageKey'

export default defineNuxtPlugin(async (nuxtApp) => {
  const userStore = useUserStore()
  const route = useRoute()
  const keys = getRuntimeAuthKeys()
  const tokenCookie = useCookie(keys.accessTokenCookie, getAuthCookieOptions())

  try {
    await userStore.initDevSession()
  } catch (e) {
    console.error('[auth plugin] initDevSession error:', e)
  }

  const opentoken = route.query.opentoken

  if (opentoken) {
    if (import.meta.dev) {
      console.warn('[auth] Detected opentoken in URL, initializing session...')
    }

    userStore.accessToken = String(opentoken)
    userStore.isLoggedIn = true
    userStore.syncAuthTokenCookie(userStore.accessToken)

    try {
      await userStore.fetchImsUserInfo()
    } catch (err) {
      console.warn('[auth] fetchImsUserInfo failed:', err)
    }

    if (import.meta.client) {
      const url = new URL(window.location.href)
      url.searchParams.delete('opentoken')
      window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    }
  } else if (tokenCookie.value && !userStore.accessToken) {
    userStore.accessToken = String(tokenCookie.value)
    userStore.isLoggedIn = true

    const refreshTokenCookie = useCookie(keys.refreshTokenCookie, getAuthCookieOptions())
    if (refreshTokenCookie.value) {
      userStore.refreshToken = String(refreshTokenCookie.value)
    }
  }

  if (import.meta.client && userStore.accessToken) {
    const tasks = []
    if (!hasStoreAgencyIdentity(userStore.userInfo)) {
      tasks.push(userStore.fetchUserInfo())
    }
    if (!userStore.userInfo) {
      tasks.push(userStore.fetchImsUserInfo())
    }
    if (tasks.length) {
      try {
        await Promise.allSettled(tasks)
      } catch (err) {
        console.warn('[auth] Auto fetch user info failed on init:', err)
      }
    }
  }

  if (import.meta.client) {
    const persistKey = keys.userPersistKey
    const handleAuthStorageChange = async (event) => {
      if (event.key === persistKey) {
        try {
          if (!event.newValue) {
            if (userStore.isLoggedIn) {
              await userStore.logout(true)
              window.location.reload()
            }
          } else {
            const newState = JSON.parse(event.newValue)
            if (newState.isLoggedIn === false && userStore.isLoggedIn) {
              await userStore.logout(true)
              window.location.reload()
            } else if (newState.isLoggedIn === true && !userStore.isLoggedIn) {
              window.location.reload()
            } else if (
              newState.isLoggedIn === true &&
              newState.accessToken &&
              (newState.accessToken !== userStore.accessToken ||
                Number(newState.tokenExpiresAt) > (Number(userStore.tokenExpiresAt) || 0) ||
                Number(newState.tokenRefreshNotBefore) >
                  (Number(userStore.tokenRefreshNotBefore) || 0))
            ) {
              // 另一个页签完成刷新时复用其结果，避免当前页签再次调用刷新接口。
              userStore.$patch({
                accessToken: newState.accessToken,
                refreshToken: newState.refreshToken || userStore.refreshToken,
                tokenExpiresAt: Number(newState.tokenExpiresAt) || null,
                refreshTokenExpiresAt:
                  Number(newState.refreshTokenExpiresAt) || userStore.refreshTokenExpiresAt,
                tokenRefreshNotBefore: Number(newState.tokenRefreshNotBefore) || 0,
                tokenRefreshNoProgressCount: Number(newState.tokenRefreshNoProgressCount) || 0,
              })
              userStore.syncAuthTokenCookie(userStore.accessToken)
              userStore.syncRefreshTokenCookie(userStore.refreshToken)
              userStore.startTokenRefreshTimer()
            }
          }
        } catch (e) {
          // ignore
        }
      }
    }

    window.addEventListener('storage', handleAuthStorageChange)
    nuxtApp.hook('app:beforeUnmount', () => {
      window.removeEventListener('storage', handleAuthStorageChange)
    })
  }
})
